'use client'

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'
import { ITelegramUser, IWebApp } from '../_types/web-app'
import { useSearchParams } from 'next/navigation'
import { toaster } from '@/components/ui/toaster'
import botService from '../_service/bot.service'
import { useDispatch, useSelector } from 'react-redux'
import { IRedux } from '../_store/types'
import { utmActions } from '../_store/utm/utm.slice'

interface ITelegramContext {
  webApp: IWebApp | null
  user: ITelegramUser | null | undefined
  tg_chat_id: string
  menu_id: string
  lang: string
  show_category_image: boolean
  only_delivery: string
  only_self_pickup: string
  branch_id: string
  client_id: string
  is_animate: boolean
  webhook_url: string
  crm_table_id: string
  crm_table_number: string
  isCartOpen: boolean
  setCartOpen: (e: boolean) => void
  openTelegramLink: (url: string) => void
}

const TelegramContext = createContext<ITelegramContext>({
  webApp: null,
  user: null,
  tg_chat_id: '',
  menu_id: '',
  lang: '',
  show_category_image: false,
  only_delivery: '',
  only_self_pickup: '',
  branch_id: '',
  client_id: '',
  is_animate: false,
  webhook_url: '',
  crm_table_id: '',
  crm_table_number: '',
  isCartOpen: false,
  setCartOpen: () => {},
  openTelegramLink: () => {},
})

type Props = {
  children: ReactNode
}

const RETRY_INTERVAL = 500
const MAX_RETRIES = 10

export const TelegramProvider = ({ children }: Props) => {
  const dispatch = useDispatch()
  const searchParams = useSearchParams()
  const [isCartOpen, setCartOpen] = useState(false)
  const [webApp, setWebApp] = useState<IWebApp | null>(null)
  const [user, setUser] = useState<ITelegramUser | null>(null)
  const [initialized, setInitialized] = useState(false)

  const utm = useSelector((store: IRedux) => store.utm)

  useEffect(() => {
    let attempts = 0
    let timeoutId: NodeJS.Timeout

    const tryInitialize = () => {
      const app = window?.Telegram?.WebApp
      const initUser = app?.initDataUnsafe?.user

      if (app && initUser?.id) {
        setWebApp(app)
        setUser(initUser || null)
        setInitialized(true)
      } else if (attempts < MAX_RETRIES) {
        attempts++
        timeoutId = setTimeout(tryInitialize, RETRY_INTERVAL)
      } else {
        console.warn('⚠️ Max retries reached. Could not init Telegram context.')
        setInitialized(true)
      }
    }

    if (typeof window !== 'undefined') {
      tryInitialize()
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [])

  const [params, setParams] = useState({
    tg_chat_id: '',
    menu_id: '',
    lang: '',
    show_category_image: false,
    only_delivery: 'true',
    only_self_pickup: 'false',
    branch_id: '',
    client_id: '',
    is_animate: false,
    webhook_url: '',
    crm_table_id: '',
    crm_table_number: '',
  })

  const openTelegramLink = (url: string) => {
    try {
      const href = url.match(/^https?:\/\//) ? url : `https://${url}`
      try {
        if (webApp?.openTelegramLink) {
          webApp.openTelegramLink(href)
          return
        } else if (typeof window !== 'undefined') {
          window.open(href, '_blank')
        }
      } catch (error) {}
    } catch (err) {
      console.warn('openTelegramLink failed:', err)
    }
  }

  const createSession = async () => {
    if (
      webApp?.initDataUnsafe?.start_param &&
      user?.id &&
      !utm?.is_session_created
    ) {
      try {
        await botService.createSession(String(user?.id))
        dispatch(utmActions.createSession())
      } catch (error) {
        console.log(error)
      }
    }
  }

  useEffect(() => {
    if (initialized) {
      const tg_chat_id = user?.id
        ? String(user.id)
        : searchParams.get('tg_chat_id') || ''

      setParams({
        tg_chat_id: String(tg_chat_id),
        menu_id: searchParams.get('menu_id') || '',
        lang: searchParams.get('lang') || '',
        show_category_image: searchParams.get('show_category_image') === 'true',
        only_delivery: searchParams.get('only_delivery') ?? 'true',
        only_self_pickup: searchParams.get('only_self_pickup') ?? 'false',
        branch_id: searchParams.get('branch_id') || '',
        client_id: searchParams.get('client_id') || '',
        is_animate: searchParams.get('is_animate') === 'true',
        webhook_url: searchParams.get('webhook_url') || '',
        crm_table_id: searchParams.get('crm_table_id') || '',
        crm_table_number: searchParams.get('crm_table_number') || '',
      })
      if (user?.id) {
        if (webApp?.LocationManager) {
          try {
            webApp?.LocationManager?.init([
              () => {
                toaster.create({
                  title: 'location manager initialized',
                  type: 'success',
                })
              },
            ])
          } catch (err) {
            console.log('location manager init failed:', err)
          }
        } else {
          toaster.create({
            title: 'location manager is not defined',
            type: 'error',
          })
        }
      }
      try {
        webApp?.enableClosingConfirmation?.()
      } catch (err) {
        console.log('enableClosingConfirmation failed:', err)
      }
      createSession()
    }
  }, [initialized, searchParams])

  return (
    <TelegramContext.Provider
      value={{
        webApp,
        user,
        ...params,
        isCartOpen,
        setCartOpen,
        openTelegramLink,
      }}
    >
      {children}
    </TelegramContext.Provider>
  )
}

export const useTelegram = () => useContext(TelegramContext)
